import { Platform, StyleSheet } from "react-native";
import { AdvertDetails } from "../../types/advert";
import AdBanner from "./AdBanners/AdBanner";
import Gap from "../Gap";
import { APP_ENV } from "../../client";
import { useAtom } from "jotai";
import { appHeightAndWidthAtom } from "../../atoms";

/**
 * Props for the CenterAdvert component.
 * @typedef {Object} CenterAdvertProps
 * @property {boolean} [gap=false] - Whether to include a vertical gap above the advert.
 * @property {AdvertDetails} [ad] - The advert details to display in the banner.
 * @property {Function} [onPress] - Callback function triggered when the advert is pressed.
 * @property {boolean} [header=false] - Whether the advert is displayed as a header.
 */

/**
 * The CenterAdvert component displays a centered advertisement banner.
 * It adjusts its layout based on the platform and environment.
 *
 * @param {CenterAdvertProps} props - The props for the CenterAdvert component.
 * @returns {JSX.Element} The rendered CenterAdvert component.
 */

type CenterAdvertProps = {
  gap?: boolean;
  ad?: AdvertDetails;
  onPress?: Function;
  header?: boolean;
};

export const CenterAdvert = ({
  gap,
  ad,
  onPress,
  header = false,
}: CenterAdvertProps) => {
  const [appHeightAndWidth] = useAtom(appHeightAndWidthAtom);

  const fullNativeWidth =
    Platform.OS == "web" ? "100vw" : appHeightAndWidth.appWidth + 6;

  const width = () => {
    if (APP_ENV === "integration" && Platform.OS !== "web") {
      return { maxWidth: 1160, width: "100%" };
    }

    // small screens
    if (Platform.OS === "ios")
      return { width: fullNativeWidth, marginLeft: -13 };

    // large screens
    return { maxWidth: 1160, width: "100%", alignSelf: "center" };
  };

  return (
    <>
      {gap && <Gap vertical />}
      <AdBanner
        onPress={onPress}
        style={[styles.centerAdvert, width()]}
        ad={ad}
        header={header}
      />
    </>
  );
};

const styles = StyleSheet.create({
  centerAdvert: {
    // justifyContent: "center",
    // alignItems: "center",
    aspectRatio: 8.14 / 1,
    marginVertical: 10,
  },
});
